package at.apa.statistictracker

import at.apa.statistictracker.configuration.Configuration
import at.apa.statistictracker.coroutines.StatisticTrackerCoroutineScope
import at.apa.statistictracker.coroutines.periodicTask
import at.apa.statistictracker.data.model.statisticevent.MpsStatisticEvent
import at.apa.statistictracker.data.model.statisticevent.StatisticEvent
import at.apa.statistictracker.data.model.userproperty.UserProperty
import at.apa.statistictracker.di.createKoinModules
import at.apa.statistictracker.logger.Logger
import at.apa.statistictracker.service.StatisticEventService
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch
import org.koin.core.context.startKoin
import org.koin.core.context.stopKoin
import org.koin.mp.KoinPlatformTools
import kotlin.js.JsExport
import kotlin.js.JsName
import kotlin.time.Duration.Companion.seconds

@JsExport
@JsName("ApaStatisticsTracker")
class ApaStatisticsTracker(
    private val configuration: Configuration
) {

    private val scope: StatisticTrackerCoroutineScope
    private val statisticEventService: StatisticEventService

    private var job: Job? = null

    init {
        // allow statistic tracker to be initialized multiple times. koin crashes for multiple initializations, therefore it must be stopped if already started.
        stopKoin()
        startKoin {
            modules(createKoinModules(configuration))
        }

        Logger.enabled = configuration.debuggable

        Logger.d(TAG, "Initializing  ApaStatisticsTracker")

        // must be initialized after starting koin
        // get() must be called on the same thread. Otherwise the library will crash on the native
        // targets because of immutability constraints (https://kotlinlang.org/docs/native-immutability.html).
        // Therefore initialize it here immediately (and not with "by lazy").
        statisticEventService = get()
        scope = get()
    }

    /**
     * Starts the periodic statistic events dispatcher task.
     *
     * Before executing the dispatch action the first time, the task waits as long as
     * configured in [Configuration.initialDispatchDelay]. Then, the wait time between the dispatch
     * actions is as long as configured in [Configuration.dispatchInterval].
     */
    @JsName("start")
    fun start() {
        Logger.d(TAG, "start()")
        if (job == null) {
            job =
                scope.periodicTask(
                    startDelay = configuration.initialDispatchDelay.seconds,
                    interval = configuration.dispatchInterval.seconds,
                    action = statisticEventService::dispatchStatisticEventsToBackend
                )
        } else {
            Logger.w(TAG, "start() - Already started")
        }
    }

    /**
     * Stops the periodic statistic events dispatcher task and flushes all already scheduled events.
     */
    @JsName("stop")
    fun stop() {
        Logger.d(TAG, "stop()")

        job?.cancel()
        job = null

        scope.launch(Dispatchers.Default) {
            statisticEventService.dispatchStatisticEventsToBackend()
        }
    }

    /**
     * Saves a [statisticEvent] to the local database.
     *
     * Locally stored events are sent to the backend by the started dispatcher task (call [start]).
     */
    @JsName("trackStatisticEvent")
    fun trackStatisticEvent(statisticEvent: StatisticEvent) {
        Logger.d(TAG, "trackStatisticEvent() with statistic event $statisticEvent")
        if (statisticEvent is MpsStatisticEvent){
            if(eventDurationIsNotValid(statisticEvent)){
                return
            }
        }
        scope.launch(Dispatchers.Default) {
            statisticEventService.trackStatisticEventLocally(statisticEvent)
        }
    }

    /**
     * Sends a [statisticEvent] directly to the configured backend ([Configuration.endpointConfig]).
     */
    @JsName("sendStatisticEvent")
    fun sendStatisticEvent(statisticEvent: StatisticEvent) {
        Logger.d(TAG, "sendStatisticEvent() with statistic event $statisticEvent")
        if (statisticEvent is MpsStatisticEvent){
            if(eventDurationIsNotValid(statisticEvent)){
                return
            }
        }
        scope.launch(Dispatchers.Default) {
            statisticEventService.sendStatisticEvent(statisticEvent)
        }
    }

    /**
     * Sets a [UserProperty].
     */
    @JsName("setUserProperty")
    fun setUserProperty(userProperty: UserProperty) {
        Logger.d(TAG, "setUserProperty() with user property $userProperty")
        scope.launch(Dispatchers.Default) {
            statisticEventService.setUserProperty(userProperty)
        }
    }

    /**
     * Checks whether event's [MpsStatisticEvent.retentionTime] is too short or not
     * For [TIMED_PRESENT_PDF_PAGE] cannot be shorter than 3 seconds,
     * For [TIMED_PRESENT_ARTICLE] and [TIMED_PRESENT_ARTICLE_IN_PDF] cannot be shorter than 5 seconds
     * None of the events should be longer than 20 minutes */
    private fun eventDurationIsNotValid(statisticEvent: MpsStatisticEvent): Boolean {
        statisticEvent.apply {
            when {
                (retentionTime ?: 0) >= EVENT_MAX_DURATION_SECONDS -> {
                    Logger.d(TAG, "Event $eventName is too long, $EVENT_MAX_DURATION_SECONDS seconds or more")
                    return true
                }
                eventName == TIMED_PRESENT_PDF_PAGE && (retentionTime ?: 0) < EVENT_PDF_PAGE_MIN_DURATION_SECONDS -> {
                    Logger.d(TAG, "Event $eventName will not be sent, duration: $retentionTime is shorter than $EVENT_PDF_PAGE_MIN_DURATION_SECONDS")
                    return true
                }
                (eventName == TIMED_PRESENT_ARTICLE_IN_PDF || eventName == TIMED_PRESENT_ARTICLE) && (retentionTime ?: 0) < EVENT_ARTICLE_MIN_DURATION_SECONDS -> {
                    Logger.d(TAG, "Event $eventName will not be sent, duration: $retentionTime is shorter than $EVENT_ARTICLE_MIN_DURATION_SECONDS")
                    return true
                }
            }
        }
        return false
    }

    private inline fun <reified T : Any> inject() =
        lazy { KoinPlatformTools.defaultContext().get().get<T>() }

    private inline fun <reified T : Any> get() = KoinPlatformTools.defaultContext().get().get<T>()

    companion object {
        private const val TAG = "ApaStatisticsTracker"
        private const val TIMED_PRESENT_PDF_PAGE = "TimedPresentPDFPage"
        private const val TIMED_PRESENT_ARTICLE = "TimedPresentArticle"
        private const val TIMED_PRESENT_ARTICLE_IN_PDF = "TimedPresentArticleInPDF"
        private const val EVENT_PDF_PAGE_MIN_DURATION_SECONDS = 3
        private const val EVENT_ARTICLE_MIN_DURATION_SECONDS = 5
        private const val EVENT_MAX_DURATION_SECONDS = 1200 // 20 min
    }
}
